import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
// Session-free client: a stale login token in localStorage must never
// poison public reads (the careers/survey "empty page" bug class).
import { publicSupabase as supabase } from '../lib/supabase';
import { isDeadlinePassed } from '../lib/format';
import JobCard from '../components/careers/JobCard';
import Reveal from '../components/ui/Reveal';
import Icon from '../components/ui/Icon';
import PatternAccent from '../components/ui/PatternAccent';
import CareersHero from '../components/careers/CareersHero';
import WhyJoinAndLife from '../components/careers/WhyJoinAndLife';
import plant from '../assets/home/plant.webp';

// The page answers, in order, the questions a candidate actually has: who are
// you, is this real, what jobs are open, do I qualify, what happens after I
// apply, and the ones they are too polite to ask (is it free? will anyone
// call me?). The old page went straight from a heading to a grid of cards.

// The six principles the acronym stands for. Named on the application form
// and in the screening, but never explained anywhere a candidate could read
// it — they were asked to commit to SANTRI before being told what it means.
const SANTRI = [
  { key: 'sopan', icon: 'handHeart' },
  { key: 'amanah', icon: 'scale' },
  { key: 'nyaman', icon: 'home' },
  { key: 'tawadhu', icon: 'humility' },
  { key: 'rajin', icon: 'sparkle' },
  { key: 'istiqomah', icon: 'repeat' },
];

const STEPS = ['choose', 'complete', 'wait'];
const FAQ_KEYS = ['free', 'contact', 'documents', 'multiple', 'official'];

function Faq({ id, t }) {
  const [open, setOpen] = useState(false);
  const bodyRef = useRef(null);

  // Animate to the answer's measured height rather than a guessed max-height:
  // the answers differ in length and translate to different heights per
  // language, so any fixed cap would either clip the long ones or make the
  // short ones ease open against empty space.
  const height = open && bodyRef.current ? bodyRef.current.scrollHeight : 0;

  return (
    <div className="border-b border-gray-200">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 py-4 text-left"
      >
        <span className="font-medium text-gray-900">{t(`careers.faq.${id}.q`)}</span>
        <svg
          className={`h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div
        style={{ height, opacity: open ? 1 : 0 }}
        className="overflow-hidden transition-all duration-300 ease-out motion-reduce:transition-none"
      >
        <p ref={bodyRef} className="pb-4 text-sm leading-relaxed text-gray-600">
          {t(`careers.faq.${id}.a`)}
        </p>
      </div>
    </div>
  );
}

export default function Careers() {
  const { t } = useTranslation();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Candidate source (BRS §9): /careers?src=instagram etc. — stash it so
  // the Apply page still knows the channel after navigation.
  useEffect(() => {
    const src = new URLSearchParams(window.location.search).get('src');
    if (src) { try { sessionStorage.setItem('wna_apply_src', src); } catch { /* noop */ } }
  }, []);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('job_positions')
        .select('*, branches(name)')
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      if (error) console.error('Error fetching positions:', error);
      // A posting past its deadline drops off the page rather than sitting
      // there with a red expired date on it. The status field stays HR's to
      // set — this only decides what a candidate is shown.
      if (data) setPositions(data.filter((p) => !isDeadlinePassed(p.apply_deadline)));
      setLoading(false);
    })();
  }, []);

  const openCount = positions.reduce((n, p) => n + (p.quantity || 1), 0);

  return (
    <div>
      <CareersHero />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <WhyJoinAndLife />

        <section className="pb-14 md:pb-20">
          {/* SANTRI, explained. The form asks candidates to commit to it and
              the screening scores them on it, so the page owes them the
              actual meaning rather than an acronym. */}
          <Reveal className="mt-8">
            <div className="relative overflow-hidden rounded-2xl border border-primary-100 bg-primary-50/50">
              <PatternAccent at="top-right" className="w-44" opacity={0.1} />
              {/* Portrait beside a single content column, rather than three
                  columns: split three ways the principle titles broke
                  mid-phrase ("Sopan Santun —" / "Courtesy"). */}
              <div className="relative z-10 p-7 md:p-9">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
                    {t('careers.santriEyebrow', 'Budaya Kerja Kami')}
                  </span>
                  <h3 className="mt-2 font-serif text-2xl font-bold text-gray-900 md:text-3xl">
                    {t('careers.santriTitle', 'Budaya SANTRI')}
                  </h3>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-gray-600">
                    {t('careers.santriBody')}
                  </p>
                <ul className="mt-7 grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
                  {SANTRI.map((s) => (
                    <li key={s.key} className="flex gap-3">
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-primary-600 shadow-sm">
                        <Icon name={s.icon} className="h-4 w-4" />
                      </span>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {t(`careers.santri.${s.key}.title`)}
                        </div>
                        <p className="mt-0.5 text-sm leading-snug text-gray-600">
                          {t(`careers.santri.${s.key}.body`)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
                </div>
              </div>
            </div>
          </Reveal>
        </section>

        {/* ---------- Open positions ---------- */}
        <section id="positions" className="relative scroll-mt-24 pb-14 md:pb-20">
          <img
            src={plant}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-6 -left-16 z-0 hidden w-40 opacity-50 lg:block"
          />
          <Reveal className="mb-8 flex flex-wrap items-end justify-between gap-3">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
                {t('careers.positionsEyebrow', 'Posisi Yang Dibuka')}
              </span>
              <h2 className="mt-2 text-2xl font-bold text-gray-900 md:text-3xl">
                {t('careers.openPositions')}
              </h2>
            </div>
            {!loading && positions.length > 0 && (
              <span className="rounded-full bg-primary-50 px-4 py-1.5 text-sm font-semibold text-primary-700">
                {t('careers.openCount', { count: openCount, defaultValue: '{{count}} posisi tersedia' })}
              </span>
            )}
          </Reveal>

          {loading ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {[0, 1].map((i) => (
                <div key={i} className="h-72 animate-pulse rounded-2xl bg-gray-100" />
              ))}
            </div>
          ) : positions.length > 0 ? (
            <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-2">
              {positions.map((position, i) => (
                <Reveal key={position.id} delay={i * 0.08} className="h-full">
                  <JobCard position={position} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl bg-gray-50 py-14 text-center">
              <svg className="mx-auto mb-4 h-14 w-14 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p className="font-medium text-gray-600">{t('careers.noPositions')}</p>
              <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500">
                {t('careers.noPositionsHint', 'Ikuti media sosial kami untuk info lowongan berikutnya.')}
              </p>
            </div>
          )}
        </section>

        {/* ---------- Process ---------- */}
        <section id="process" className="scroll-mt-24 border-t border-gray-100 py-14 md:py-20">
          <Reveal className="mb-10 max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
              {t('careers.processEyebrow', 'Proses Pendaftaran')}
            </span>
            <h2 className="mt-2 text-2xl font-bold text-gray-900 md:text-3xl">
              {t('careers.processTitle', 'Tiga langkah sederhana.')}
            </h2>
            <p className="mt-3 text-gray-600">
              {t('careers.processBody', 'Isi setiap data dengan jujur dan benar.')}
            </p>
          </Reveal>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {STEPS.map((step, i) => (
              <Reveal key={step} delay={i * 0.1}>
                <div className="relative h-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                  <div className="mb-3 text-3xl font-bold text-primary-100">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 className="font-semibold text-gray-900">{t(`careers.steps.${step}.title`)}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
                    {t(`careers.steps.${step}.body`)}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ---------- FAQ ---------- */}
        <section className="relative border-t border-gray-100 py-14 md:py-20">
          <PatternAccent at="bottom-right" className="w-52" opacity={0.09} />
          <div className="relative z-10 grid grid-cols-1 gap-10 lg:grid-cols-3">
            <Reveal from="left">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">FAQ</span>
              <h2 className="mt-2 text-2xl font-bold text-gray-900 md:text-3xl">
                {t('careers.faqTitle', 'Yang sering ditanyakan.')}
              </h2>
            </Reveal>
            <Reveal from="right" className="lg:col-span-2">
              <div>
                {FAQ_KEYS.map((id) => <Faq key={id} id={id} t={t} />)}
              </div>
            </Reveal>
          </div>
        </section>
      </div>

      {/* ---------- Closing CTA ---------- */}
      <section className="bg-primary-600">
        <div className="mx-auto max-w-7xl px-4 py-14 text-center sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="text-2xl font-bold text-white md:text-3xl">
              {t('careers.ctaTitle', 'Siap memulai langkah terbaik Anda?')}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-primary-100">
              {t('careers.ctaBody', 'Pilih posisi yang sesuai dan kirim lamaran Anda hari ini.')}
            </p>
            <a
              href="#positions"
              className="mt-7 inline-block rounded-lg bg-white px-8 py-3 font-semibold text-primary-700 transition-transform hover:scale-105"
            >
              {t('careers.seePositions', 'Lihat Lowongan')}
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
